import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";

import MainContainer from "../../components/MainContainer";

import {
  selectCoverForTrainModePending,
  selectDeleteTrainModelPending,
  selectSelectedTrainModel,
  setSelectedCollection,
} from "../../store/slices/model/slice";
import { COLLECTION_STATUS } from "../../utils/constants";
import GenerateCollectionModal from "../../components/GenerateCollectionModal/GenerateCollectionModal";
import EditImageModal from "../../components/EditImageModal/EditImageModal";

import {
  BackButton,
  ModelTitle,
  ConfigSection,
  TutorialLink,
  SectionTitle,
  Label,
  TextInput,
  RequirementsSection,
  RequirementText,
  UploadSection,
  UploadCount,
  ActionButtons,
  DeleteButton,
  InputContainer,
  Row,
  Text,
  LabelContainer,
  FilePreviewGrid,
  FilePreview,
  FilePreviewItem,
  SkeletonLoaderColumn,
  SkeletonLoader,
  SkeletonSection,
  RowSpaceBetween,
  VideoWrapper,
  SelectButton,
  ProgressOverlay,
  EmptyStateContainer,
  EmptyStateText,
  EmptyStateSubtext,
  SectionHeader,
  SectionWrapper,
  LinkText,
  LabelColumn,
  TextAreaInput,
} from "./TrainingSelectCoverPage.styles";

import { ReactComponent as ChevronLeft } from "../../assets/icons/chevronLeft.svg";
import { ReactComponent as SettingsSvg } from "../../assets/icons/settings.svg";
import { ReactComponent as CardIconSvg } from "../../assets/icons/cardIcon.svg";
import { ReactComponent as StarSvg } from "../../assets/icons/whiteStar.svg";
import { ReactComponent as MagicStickSvg } from "../../assets/icons/magicStick.svg";
import { ReactComponent as TrashSvg } from "../../assets/icons/trash.svg";
import LottieIcon from "../../components/LottieIcon/LottieIcon";
import radioButtonAnimation from "../../assets/animations/radioButton.json";
import TrainingProgressComponent from "../../components/utils/TrainingProgressComponent/TrainingProgressComponent";
import Button from "../../components/utils/Button/Button";
import Loader from "../../components/utils/Loader/Loader";
import {
  deleteTrainModel,
  getTrainModel,
  selectCoverForTrainModel,
} from "../../store/slices/model/asyncThunks";
import { selectLoggedIn } from "../../store/slices/auth/slice";
import { toast } from "../../components/modern-ui/sonner";
import { selectUser } from "../../store/slices/user/slice";
import ConfirmationModal from "../../components/ConfirmationModal/ConfirmationModal";

const TrainingSelectCoverPage = () => {
  const navigate = useNavigate();
  const params = useParams();
  const dispatch = useDispatch();

  const selectedTrainModel = useSelector(selectSelectedTrainModel);
  const isLoggedIn = useSelector(selectLoggedIn);

  const trainModelId = params.modelId;

  const selectCoverLoading = useSelector(selectCoverForTrainModePending);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const deleteTrainModelLoading = useSelector(selectDeleteTrainModelPending);

  const [taskName, setTaskName] = useState("");
  const [templateName, setTemplateName] = useState("");
  const [prompt, setPrompt] = useState("");

  const [selectCover, setSelectCover] = useState(null);
  const [isUseModelModalOpen, setIsUseModelModalOpen] = useState(false);
  const user = useSelector(selectUser);

  // Training type: "video" | "image" | "person"
  const trainingType = selectedTrainModel.data?.type || "video";

  const allCollections = selectedTrainModel.data?.collections || [];
  const visibleCollections = allCollections.filter(
    (c) => c.status !== COLLECTION_STATUS.FAILED,
  );
  const firstCompletedCollection = visibleCollections.find(
    (c) => c.status === COLLECTION_STATUS.COMPLETED,
  );

  useEffect(() => {
    if (isLoggedIn && trainModelId) {
      dispatch(getTrainModel(trainModelId));
    }
  }, [trainModelId, isLoggedIn, dispatch]);

  useEffect(() => {
    if (selectedTrainModel.data) {
      const modelName = selectedTrainModel?.data?.name || "";
      const modelPrompt = selectedTrainModel?.data?.prompt || "";

      setTaskName(modelName);
      setTemplateName(modelName);
      setPrompt(modelPrompt);
    }
  }, [selectedTrainModel.data]);

  useEffect(() => {
    if (firstCompletedCollection && !selectCover) {
      setSelectCover(firstCompletedCollection._id);
    }
  }, [firstCompletedCollection, selectCover]);

  const handleOpenUseModel = () => {
    const source = firstCompletedCollection || allCollections[0];
    if (!source) {
      toast.error("No cover available to use as source.");
      return;
    }
    const modelCredits = selectedTrainModel.data?.model?.credits;
    const sourceWithPrice = {
      ...source,
      price:
        source.price ??
        source.pricing?.model ??
        source.pricing?.total ??
        modelCredits ??
        0,
    };

    if (trainingType === "video") {
      dispatch(setSelectedCollection(sourceWithPrice));
    }
    setIsUseModelModalOpen(true);
  };

  const handleCloseUseModel = () => {
    setIsUseModelModalOpen(false);
  };

  const handleBack = () => {
    navigate("/train");
  };

  const handleTutorialClick = () => {
    navigate("/train-tutorial");
  };

  const handleSelect = (coverId) => {
    setSelectCover(coverId);
  };

  const hasDefaultTemplateName = /new template/i.test(templateName);

  const actionSelectCover = () => {
    if (!selectCover || !templateName || templateName.length < 3) {
      toast.error(
        `Please select a cover video and enter a template name with at least 3 characters.`,
      );
      return;
    }

    if (hasDefaultTemplateName) {
      toast.error(
        `Please change the template name — it still contains the default "New Template".`,
      );
      return;
    }

    dispatch(
      selectCoverForTrainModel({
        trainModelId,
        collectionId: selectCover,
        templateName,
      }),
    )
      .unwrap()
      .then(() => {
        navigate(`/train/completed/${trainModelId}`);
      })
      .catch(() => {
        toast.error("Error occurred while selecting cover video");
      });
  };

  const handleDelete = () => {
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await dispatch(deleteTrainModel(trainModelId)).unwrap();
      navigate("/train");
      localStorage.setItem("activeTabIndex", "2");
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: "instant",
      });
    } catch (error) {
      console.error("Failed to delete train model:", error);
    }
    setIsDeleteModalOpen(false);
  };

  const handleCancelDelete = () => {
    setIsDeleteModalOpen(false);
  };

  // =============================================
  // Render: Media preview (video or image) based on training type
  // =============================================

  const renderMediaPreview = (fileObj) => {
    if (trainingType === "person" || trainingType === "image") {
      const imageUrl =
        fileObj?.output?.image?.url || fileObj?.output?.video?.url;
      return (
        <img
          src={imageUrl}
          alt="preview"
          width="100%"
          height="auto"
          style={{ display: "block" }}
        />
      );
    }

    // Default: video (for "video" type and any future types)
    return (
      <video
        src={fileObj.output.video.url}
        muted
        controls
        preload="metadata"
        width="100%"
        height="auto"
        style={{ display: "block" }}
      />
    );
  };

  // =============================================
  // Render: Requirements section by training type
  // =============================================

  const renderRequirements = () => {
    if (trainingType === "video") {
      return (
        <RequirementsSection>
          <SectionHeader>
            <SectionTitle>Requirements</SectionTitle>
            <CardIconSvg />
          </SectionHeader>

          <SectionWrapper>
            <LabelColumn>
              <LabelContainer>
                <Label>Training Videos</Label>
                <UploadCount>10-20 videos</UploadCount>
              </LabelContainer>
              <Text>
                Upload 10-20 videos showing transition cuts from still picture
                to motion video. Each video should be between 3 to 5 seconds
                long, with a minimum resolution of 360p.
              </Text>
            </LabelColumn>
            <LabelColumn>
              <LabelContainer>
                <Label>Cost</Label>
                <UploadCount>
                  <StarSvg />
                  {!user?.hasTrainedModel
                    ? "300 Credits (67% Off)"
                    : user?.isVipCreator || user?.numOfAcceptedModels > 15
                      ? "500 Credits"
                      : "900 Credits"}
                </UploadCount>
              </LabelContainer>
              <Text>
                Training cost is deducted when you start the training. Each user
                has only one opportunity for a discounted trial.
              </Text>
            </LabelColumn>
          </SectionWrapper>
        </RequirementsSection>
      );
    }

    if (trainingType === "image") {
      return (
        <RequirementsSection>
          <SectionHeader>
            <SectionTitle>Requirements</SectionTitle>
            <CardIconSvg />
          </SectionHeader>

          <SectionWrapper>
            <LabelColumn>
              <LabelContainer>
                <Label>Dataset</Label>
                <UploadCount>15-20 pairs</UploadCount>
              </LabelContainer>
              <Text>
                Upload 15-20 before &amp; after examples of images. Each pair
                should contain a dataset image and a corresponding control
                image.
              </Text>
            </LabelColumn>

            <LabelColumn>
              <LabelContainer>
                <Label>Testing Images</Label>
                <UploadCount>3-6 images</UploadCount>
              </LabelContainer>
              <Text>
                Select 3-6 images from the library to test the trained template.
              </Text>
            </LabelColumn>

            <LabelColumn>
              <LabelContainer>
                <Label>Cost</Label>
                <UploadCount>
                  <StarSvg />
                  {"400 Credits"}
                </UploadCount>
              </LabelContainer>
              <Text>
                Training cost is deducted when you start the training.
                <br />
                Each User has only one opportunity for a discounted trial.
              </Text>
            </LabelColumn>
          </SectionWrapper>
        </RequirementsSection>
      );
    }

    if (trainingType === "person") {
      return (
        <RequirementsSection>
          <SectionHeader>
            <SectionTitle>Requirements</SectionTitle>
            <CardIconSvg />
          </SectionHeader>

          <SectionWrapper>
            <LabelColumn>
              <LabelContainer>
                <Label>Images</Label>
                <UploadCount>20-40 photos</UploadCount>
              </LabelContainer>
              <Text>
                Upload 20-40 high quality photos of the same person from
                different angles.
              </Text>
            </LabelColumn>

            <LabelColumn>
              <LabelContainer>
                <Label>Cost</Label>
                <UploadCount>
                  <StarSvg />
                  {!user?.hasTrainedModel
                    ? "300 Credits (67% Off)"
                    : user?.isVipCreator || user?.numOfAcceptedModels > 15
                      ? "500 Credits"
                      : "900 Credits"}
                </UploadCount>
              </LabelContainer>
              <Text>
                Training cost is deducted when you start the training.
                <br />
                Each User has only one opportunity for a discounted trial.
              </Text>
            </LabelColumn>
          </SectionWrapper>
        </RequirementsSection>
      );
    }
  };

  // =============================================
  // Render: Configuration section by training type
  // =============================================

  const renderConfiguration = () => (
    <ConfigSection>
      <SectionHeader>
        <SectionTitle>Configuration</SectionTitle>
        <SettingsSvg width={24} height={24} />
      </SectionHeader>
      <SectionWrapper>
        <TutorialLink onClick={handleTutorialClick}>
          <LinkText>📚 View our training tutorial to get started.</LinkText>
          <ChevronLeft />
        </TutorialLink>

        <InputContainer>
          <Label>Task name</Label>
          <TextInput
            type="text"
            placeholder="New Template 01"
            value={taskName}
            onChange={(e) => setTaskName(e.target.value)}
            disabled={true}
          />
        </InputContainer>

        {/* Prompt is shown for video and image, but NOT for person */}
        {trainingType !== "person" && (
          <InputContainer>
            <Label>Prompt</Label>
            <TextAreaInput
              type="text"
              placeholder="e.g. 'A sentence that clearly and accurately describes the motion in your training videos.'"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              disabled={true}
            />
            <RequirementText>
              A sentence that clearly and accurately describes the motion in
              your training videos.
            </RequirementText>
          </InputContainer>
        )}
      </SectionWrapper>
    </ConfigSection>
  );

  // Show loading state
  if (selectedTrainModel.loading) {
    return (
      <MainContainer>
        <BackButton onClick={handleBack}>
          <ChevronLeft />
          Back to Train
        </BackButton>
        <SkeletonLoaderColumn>
          <SkeletonLoader $height="26px" $width="200px" $radius="4px" />
          <SkeletonLoader $height="26px" $width="400px" $radius="4px" />
          <RowSpaceBetween>
            <SkeletonLoader $height="16px" $width="600px" $radius="4px" />
            <SkeletonLoader $height="20px" $width="40px" $radius="4px" />
          </RowSpaceBetween>
          <SkeletonLoader $height="5px" $width="100%" $radius="100px" />

          <Row>
            <SkeletonSection $height="335px">
              <SkeletonLoader $height="22px" $width="150px" />
              <SkeletonLoader $height="44px" $width="100%" $radius="12px" />
              <SkeletonLoader $height="16px" $width="100%" />
              <SkeletonLoader $height="44px" $width="100%" $radius="12px" />
              <SkeletonLoader $height="44px" $width="100%" $radius="12px" />
            </SkeletonSection>

            <SkeletonSection $height="335px">
              <SkeletonLoader $height="22px" $width="180px" />
              <SkeletonLoader $height="16px" $width="100%" />
              <SkeletonLoader $height="16px" $width="100%" />
              <SkeletonLoader $height="16px" $width="100%" />
              <SkeletonLoader $height="16px" $width="100%" />
            </SkeletonSection>
          </Row>

          <SkeletonSection $height="669px">
            <SkeletonLoader $height="27px" $width="140px" />

            <SkeletonLoader $height="22px" $width="160px" />
            <SkeletonLoader $height="44px" $width="500px" $radius="12px" />
            <SkeletonLoader $height="16px" $width="350px" />

            <SkeletonLoader $height="22px" $width="160px" />
            <SkeletonLoader $height="16px" $width="450px" />

            <SkeletonLoader
              style={{ marginTop: 16 }}
              $height="136px"
              $width="100%"
              $radius="16px"
            />
          </SkeletonSection>
        </SkeletonLoaderColumn>
      </MainContainer>
    );
  }

  return (
    <MainContainer>
      <BackButton onClick={handleBack}>
        <ChevronLeft />
        Back to Train
      </BackButton>

      <ModelTitle>{taskName || "New Template 01"}</ModelTitle>

      <TrainingProgressComponent
        progress={75}
        title="Configure your Template"
        subtitle="Configure your template and select the cover video"
      />

      <Row>
        {renderConfiguration()}
        {renderRequirements()}
      </Row>

      <UploadSection>
        <SectionHeader>
          <SectionTitle>Configuration</SectionTitle>
          <SettingsSvg width={24} height={24} />
        </SectionHeader>
        <SectionWrapper>
          <InputContainer $maxWidth>
            <Label>Template Name</Label>
            <TextInput
              type="text"
              placeholder="Enter template name"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
            />
            <RequirementText>
              This will be the name of your template
            </RequirementText>
          </InputContainer>
          <InputContainer>
            <RowSpaceBetween>
              <LabelColumn>
                <Label>
                  {trainingType === "person" || trainingType === "image"
                    ? "Select Cover Image"
                    : "Select Cover Video"}
                </Label>
                <Text>
                  {trainingType === "person" || trainingType === "image"
                    ? "Choose the best image as the cover for your template (from testing results)"
                    : "Choose the best video as the cover for your template (from testing results)"}
                </Text>
              </LabelColumn>
              {user?.isVipCreator && trainingType !== "person" && (
                <Button
                  onClick={handleOpenUseModel}
                  title={"Use Model"}
                  leftIcon={<MagicStickSvg />}
                  style={{ width: "auto", padding: "12px 20px" }}
                />
              )}
            </RowSpaceBetween>
          </InputContainer>
          {!!selectedTrainModel.data &&
            !!selectedTrainModel.data.collections && (
              <>
                {visibleCollections.length > 0 ? (
                  <FilePreviewGrid>
                    {visibleCollections.map((fileObj) => {
                      const isSelected = selectCover === fileObj._id;
                      const isInProgress =
                        fileObj.status !== COLLECTION_STATUS.COMPLETED;
                      return (
                        <FilePreviewItem
                          key={fileObj._id}
                          $selected={isSelected}
                        >
                          <VideoWrapper>
                            {!isInProgress && (
                              <SelectButton
                                $selected={isSelected}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleSelect(fileObj._id);
                                }}
                                type="button"
                              >
                                <LottieIcon
                                  animationData={radioButtonAnimation}
                                  trigger="toggle-reverse"
                                  speed={2}
                                  isActive={isSelected}
                                  size={22}
                                  strokeColor={"#ffffff"}
                                />
                              </SelectButton>
                            )}

                            <FilePreview>
                              {isInProgress ? (
                                <ProgressOverlay>
                                  <Loader size={48} text="Generating" />
                                </ProgressOverlay>
                              ) : (
                                renderMediaPreview(fileObj)
                              )}
                            </FilePreview>
                          </VideoWrapper>
                        </FilePreviewItem>
                      );
                    })}
                  </FilePreviewGrid>
                ) : (
                  <EmptyStateContainer>
                    <EmptyStateText>
                      {trainingType === "person" || trainingType === "image"
                        ? "No images available"
                        : "No videos available"}
                    </EmptyStateText>
                    <EmptyStateSubtext>
                      {trainingType === "person" || trainingType === "image"
                        ? "There are no test images generated yet for this model."
                        : "There are no test videos generated yet for this model."}
                    </EmptyStateSubtext>
                  </EmptyStateContainer>
                )}
              </>
            )}
        </SectionWrapper>
      </UploadSection>

      <ActionButtons>
        <DeleteButton
          onClick={handleDelete}
          leftIcon={<TrashSvg width={15} height={15} />}
          title={"Delete"}
          style={{
            borderRadius: "100px",
            display: "flex",
            width: "130px",
            height: "44px",
            justifyContent: "center",
            alignItems: "center",
            gap: "12px",
            background: "transparent",
            border: "none",
            outline: "none",
            color: "#f2efe8",
            cursor: "pointer",
            fontFamily: "Figtree, sans-serif",
            fontSize: "16px",
            fontWeight: 500,
            letterSpacing: "-0.08px",
            transition: "all 0.3s ease-in-out",
          }}
        />
        <Button
          onClick={actionSelectCover}
          loading={selectCoverLoading}
          title={"Complete Training"}
          leftIcon={<MagicStickSvg />}
        />
      </ActionButtons>

      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title="Delete Model"
        isLoading={deleteTrainModelLoading}
        message="Are you sure you want to delete this train model? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
      />

      {/* TODO: handle "person" training type when backend/modals support it */}
      {trainingType === "video" && (
        <GenerateCollectionModal
          isOpen={isUseModelModalOpen}
          onClose={handleCloseUseModel}
          fromTrainingSelectCoverPage
        />
      )}

      {trainingType === "image" && (
        <EditImageModal
          isOpen={isUseModelModalOpen}
          onClose={handleCloseUseModel}
          collection={firstCompletedCollection || allCollections[0]}
          fromTrainingSelectCoverPage
        />
      )}
    </MainContainer>
  );
};

export default TrainingSelectCoverPage;
